import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { invoicesAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";
import InvoiceDocumentModal from "./InvoiceDocumentModal";
import InvoiceTransfersOverview from "./InvoicesTransfersOverwiev";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSpinner,
    faSyncAlt,
    faSearch,
    faHandPointer,
    faCheck,
    faTimes,
    faFile,
    faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";

import "../../../css/Views/InvoicesRegisterView.css";


const monthNames = [
    "Styczeń",
    "Luty",
    "Marzec",
    "Kwiecień",
    "Maj",
    "Czerwiec",
    "Lipiec",
    "Sierpień",
    "Wrzesień",
    "Październik",
    "Listopad",
    "Grudzień"
];


const InvoicesRegisterView = () => {
    const { callApi, isLoading, error } = useApi();

    const now = new Date();
    const currentYear = now.getFullYear();

    const availableYears = Array.from(
        { length: currentYear - 2025 + 1 },
        (_, index) => currentYear - index
    );

    const [activeTab, setActiveTab] = useState("invoices");

    const [year, setYear] = useState(currentYear);
    const [month, setMonth] = useState(now.getMonth() + 1);

    const [documents, setDocuments] = useState([]);

    const [statusFilter, setStatusFilter] = useState("all");
    const [konektoFilter, setKonektoFilter] = useState("all");
    const [invoiceSearch, setInvoiceSearch] = useState("");

    const [transferStatusFilter, setTransferStatusFilter] = useState("all");
    const [transferSearch, setTransferSearch] = useState("");

    const [selectedDocumentId, setSelectedDocumentId] = useState(null);
    const [updatingDocumentId, setUpdatingDocumentId] = useState(null);

    const [isSyncing, setIsSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);

    const [sortBy, setSortBy] = useState("issueDate");
    const [sortDirection, setSortDirection] = useState("desc");


    const getStatusApiValue = () => {
        if (statusFilter === "all")
            return null;

        return Number(statusFilter);
    };


    const getKonektoApiValue = () => {
        if (konektoFilter === "uploaded")
            return true;

        if (konektoFilter === "missing")
            return false;

        return null;
    };


    const loadDocuments = () => {
        callApi(
            () => invoicesAPI.getAll({
                year,
                month,
                status: getStatusApiValue(),
                isUploadedToKonektoSmart: getKonektoApiValue()
            }),
            (data) => {
                setDocuments(Array.isArray(data) ? data : []);
            }
        );
    };


    const handleRefresh = () => {
        if (isSyncing)
            return;

        setIsSyncing(true);
        setSyncProgress(null);

        callApi(
            () => invoicesAPI.sync(year, month),
            () => {
                callApi(
                    () => invoicesAPI.getAll({
                        year,
                        month,
                        status: getStatusApiValue(),
                        isUploadedToKonektoSmart: getKonektoApiValue()
                    }),
                    (data) => {
                        setDocuments(Array.isArray(data) ? data : []);
                        setIsSyncing(false);
                        setSyncProgress(null);
                    },
                    () => {
                        setIsSyncing(false);
                        setSyncProgress(null);
                    }
                );
            },
            () => {
                setIsSyncing(false);
                setSyncProgress(null);
            }
        );
    };


    useEffect(() => {
        if (!isSyncing)
            return;

        const loadSyncProgress = async () => {
            try {
                const response = await invoicesAPI.getSyncProgress();
                const progress = response.data;

                if (progress.isRunning)
                    setSyncProgress(progress);
            } catch {
            }
        };

        loadSyncProgress();

        const interval = setInterval(loadSyncProgress, 1000);

        return () => clearInterval(interval);
    }, [isSyncing]);


    useEffect(() => {
        if (activeTab !== "invoices")
            return;

        loadDocuments();
    }, [
        year,
        month,
        statusFilter,
        konektoFilter,
        activeTab
    ]);


    const handleYearChange = (event) => {
        const value = event.target.value;

        if (value === "all") {
            setYear(null);
            setMonth(null);
            return;
        }

        setYear(Number(value));
    };


    const handleMonthChange = (event) => {
        const value = event.target.value;

        if (value === "all") {
            setMonth(null);
            return;
        }

        setMonth(Number(value));
    };


    const getPaymentStatusDisplay = (document) => {
        switch (document.paymentStatus) {
            case 1:
                return {
                    text: "Nieopłacona",
                    className: "invoice-status-unpaid"
                };

            case 2:
                return {
                    text: "Częściowo",
                    className: "invoice-status-partial"
                };

            case 3:
                return {
                    text: "Opłacona",
                    className: "invoice-status-paid",
                    isManual: document.isPaymentManuallyConfirmed
                };

            case 4:
                return {
                    text: "Do sprawdzenia",
                    className: "invoice-status-review"
                };

            default:
                return null;
        }
    };


    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection((current) =>
                current === "asc" ? "desc" : "asc"
            );

            return;
        }

        setSortBy(column);
        setSortDirection("asc");
    };


    const getSortIndicator = (column) => {
        if (sortBy !== column)
            return null;

        return sortDirection === "asc"
            ? " ↑"
            : " ↓";
    };


    const sortedDocuments = useMemo(() => {
        const normalizedSearch = invoiceSearch
            .trim()
            .toLocaleLowerCase("pl-PL")
            .replace(",", ".");

        const filteredDocuments = normalizedSearch
            ? documents.filter((document) => {
                const searchableValues = [
                    document.fileName,
                    document.contractor,
                    document.contractorNip,
                    document.nip,
                    document.documentNumber,
                    document.ksefNumber,
                    document.grossAmount,
                    document.notes
                ];

                return searchableValues.some((value) =>
                    String(value ?? "")
                        .toLocaleLowerCase("pl-PL")
                        .replace(",", ".")
                        .includes(normalizedSearch)
                );
            })
            : documents;

        return [...filteredDocuments].sort((a, b) => {
            let valueA;
            let valueB;

            switch (sortBy) {
                case "contractor":
                    valueA = a.contractor ?? "";
                    valueB = b.contractor ?? "";
                    break;

                case "documentNumber":
                    valueA = a.documentNumber ?? "";
                    valueB = b.documentNumber ?? "";
                    break;

                case "issueDate":
                    valueA = a.issueDate
                        ? new Date(a.issueDate).getTime()
                        : 0;

                    valueB = b.issueDate
                        ? new Date(b.issueDate).getTime()
                        : 0;
                    break;

                case "grossAmount":
                    valueA = a.grossAmount ?? 0;
                    valueB = b.grossAmount ?? 0;
                    break;

                case "konektoSmart":
                    valueA = a.isUploadedToKonektoSmart === true
                        ? 1
                        : 0;

                    valueB = b.isUploadedToKonektoSmart === true
                        ? 1
                        : 0;
                    break;

                case "status":
                    valueA = getPaymentStatusDisplay(a)?.text ?? "";
                    valueB = getPaymentStatusDisplay(b)?.text ?? "";
                    break;

                case "notes":
                    valueA = a.notes ?? "";
                    valueB = b.notes ?? "";
                    break;

                case "fileName":
                default:
                    valueA = a.fileName ?? "";
                    valueB = b.fileName ?? "";
                    break;
            }

            let result;

            if (
                typeof valueA === "string" &&
                typeof valueB === "string"
            ) {
                result = valueA.localeCompare(
                    valueB,
                    "pl",
                    {
                        sensitivity: "base",
                        numeric: true
                    }
                );
            } else {
                result = valueA - valueB;
            }

            return sortDirection === "asc"
                ? result
                : -result;
        });
    }, [
        documents,
        invoiceSearch,
        sortBy,
        sortDirection
    ]);


    const formatDate = (value) => {
        if (!value)
            return "—";

        return new Date(value)
            .toLocaleDateString("pl-PL");
    };


    const formatAmount = (amount, currency = "PLN") => {
        if (amount == null)
            return "—";

        return new Intl.NumberFormat(
            "pl-PL",
            {
                style: "currency",
                currency: currency || "PLN"
            }
        ).format(amount);
    };


    const handleDocumentClick = (documentId) => {
        if (updatingDocumentId === documentId)
            return;

        setSelectedDocumentId(documentId);
    };


    const handleCloseModal = () => {
        setSelectedDocumentId(null);
    };


    const handleDocumentSaving = (documentId) => {
        setUpdatingDocumentId(documentId);
    };


    const handleDocumentSaved = (updatedDocument) => {
        setDocuments((current) =>
            current.map((document) =>
                document.id === updatedDocument.id
                    ? updatedDocument
                    : document
            )
        );

        setUpdatingDocumentId(null);
    };


    const handleDocumentSaveError = () => {
        setUpdatingDocumentId(null);
    };


    const handleDocumentRelationsChanged = () => {
        loadDocuments();
    };


    const openDriveFile = (event, googleDriveFileId) => {
        event.stopPropagation();

        if (!googleDriveFileId)
            return;

        window.open(
            `https://drive.google.com/file/d/${googleDriveFileId}/view`,
            "_blank",
            "noopener,noreferrer"
        );
    };


    return (
        <div className="invoices-register">

            <div className="invoices-register-header">
                Rejestr faktur
            </div>


            <div className="invoice-register-tabs">

                <div
                    className={`invoice-register-tab ${
                        activeTab === "invoices"
                            ? "invoice-register-tab-active"
                            : ""
                    }`}
                    onClick={() => setActiveTab("invoices")}
                >
                    Faktury
                </div>

                <div
                    className={`invoice-register-tab ${
                        activeTab === "transfers"
                            ? "invoice-register-tab-active"
                            : ""
                    }`}
                    onClick={() => setActiveTab("transfers")}
                >
                    Przelewy
                </div>

            </div>


            <div className="invoice-filters-panel">

                <div className="invoice-filters-main">

                    <div className="invoice-filter-field invoice-filter-year">
                        <span className="invoice-filter-label">
                            Rok
                        </span>

                        <select
                            value={year ?? "all"}
                            onChange={handleYearChange}
                        >
                            <option value="all">
                                Wszystkie
                            </option>

                            {availableYears.map(
                                (availableYear) => (
                                    <option
                                        key={availableYear}
                                        value={availableYear}
                                    >
                                        {availableYear}
                                    </option>
                                )
                            )}
                        </select>
                    </div>


                    <div className="invoice-filter-field invoice-filter-month">
                        <span className="invoice-filter-label">
                            Miesiąc
                        </span>

                        <select
                            value={month ?? "all"}
                            onChange={handleMonthChange}
                            disabled={year == null}
                        >
                            <option value="all">
                                Wszystkie
                            </option>

                            {monthNames.map(
                                (
                                    monthName,
                                    index
                                ) => (
                                    <option
                                        key={monthName}
                                        value={index + 1}
                                    >
                                        {monthName}
                                    </option>
                                )
                            )}
                        </select>
                    </div>


                    {activeTab === "invoices" && (
                        <>
                            <div className="invoice-filter-field invoice-filter-status">
                                <span className="invoice-filter-label">
                                    Status
                                </span>

                                <select
                                    value={statusFilter}
                                    onChange={(e) =>
                                        setStatusFilter(
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="all">
                                        Wszystkie
                                    </option>

                                    <option value="1">
                                        Opłacona
                                    </option>

                                    <option value="2">
                                        Częściowo opłacona
                                    </option>

                                    <option value="3">
                                        Nieopłacona
                                    </option>

                                    <option value="4">
                                        Do sprawdzenia
                                    </option>

                                    <option value="5">
                                        Nowe
                                    </option>

                                    <option value="6">
                                        Ignorowana
                                    </option>

                                    <option value="7">
                                        Plik usunięty
                                    </option>
                                </select>
                            </div>


                            <div className="invoice-filter-field invoice-filter-konekto">
                                <span className="invoice-filter-label">
                                    KonektoSmart
                                </span>

                                <select
                                    value={konektoFilter}
                                    onChange={(e) =>
                                        setKonektoFilter(
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="all">
                                        Wszystkie
                                    </option>

                                    <option value="missing">
                                        Niewczytane
                                    </option>

                                    <option value="uploaded">
                                        Wczytane
                                    </option>
                                </select>
                            </div>


                            <div className="invoice-filter-field invoice-filter-search">
                                <span className="invoice-filter-label">
                                    Szukaj
                                </span>

                                <div className="invoice-filter-search-wrapper">
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="invoice-filter-search-icon"
                                    />

                                    <input
                                        type="text"
                                        value={invoiceSearch}
                                        onChange={(e) =>
                                            setInvoiceSearch(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Kontrahent, numer, plik, NIP lub kwota..."
                                    />
                                </div>
                            </div>
                        </>
                    )}


                    {activeTab === "transfers" && (
                        <>
                            <div className="invoice-filter-field invoice-filter-status">
                                <span className="invoice-filter-label">
                                    Status
                                </span>

                                <select
                                    value={transferStatusFilter}
                                    onChange={(e) =>
                                        setTransferStatusFilter(
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="all">
                                        Wszystkie
                                    </option>

                                    <option value="unmatched">
                                        Bez dopasowania
                                    </option>

                                    <option value="matched">
                                        1 faktura
                                    </option>

                                    <option value="multiple">
                                        Wiele faktur
                                    </option>

                                    <option value="noInvoice">
                                        Nie wymaga faktury
                                    </option>
                                </select>
                            </div>


                            <div className="invoice-filter-field invoice-filter-search">
                                <span className="invoice-filter-label">
                                    Szukaj
                                </span>

                                <div className="invoice-filter-search-wrapper">
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="invoice-filter-search-icon"
                                    />

                                    <input
                                        type="text"
                                        value={transferSearch}
                                        onChange={(e) =>
                                            setTransferSearch(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Kontrahent, tytuł lub kwota..."
                                    />
                                </div>
                            </div>
                        </>
                    )}

                </div>


                {activeTab === "invoices" && (
                    <div className="invoice-filters-actions">

                        <div
                            className={`invoice-refresh-action ${
                                isSyncing
                                    ? "invoice-refresh-action-disabled"
                                    : ""
                            }`}
                            onClick={handleRefresh}
                        >
                            {isSyncing ? (
                                <>
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        spin
                                    />

                                    {syncProgress?.total > 0
                                        ? `Synchronizacja ${syncProgress.processed} / ${syncProgress.total}`
                                        : "Synchronizacja..."}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faSyncAlt}
                                    />

                                    Synchronizuj
                                </>
                            )}
                        </div>

                    </div>
                )}

            </div>


            {activeTab === "invoices" && (
                <>

                    {isLoading && !isSyncing && (
                        <Loading text="Ładowanie dokumentów..." />
                    )}


                    {error && (
                        <p className="error">
                            Błąd: {error.message}
                        </p>
                    )}


                    {!isLoading &&
                        sortedDocuments.length === 0 && (
                            <div className="empty-message">
                                Brak dokumentów dla wybranych filtrów.
                            </div>
                        )}


                    {!isLoading &&
                        sortedDocuments.length > 0 && (
                            <div className="invoice-register-table-wrapper">

                                <table className="invoice-register-table">

                                    <thead>
                                        <tr>

                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "fileName"
                                                    )
                                                }
                                            >
                                                Plik
                                                {getSortIndicator(
                                                    "fileName"
                                                )}
                                            </th>


                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "issueDate"
                                                    )
                                                }
                                            >
                                                Wystawiono
                                                {getSortIndicator(
                                                    "issueDate"
                                                )}
                                            </th>


                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "contractor"
                                                    )
                                                }
                                            >
                                                Kontrahent
                                                {getSortIndicator(
                                                    "contractor"
                                                )}
                                            </th>


                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "documentNumber"
                                                    )
                                                }
                                            >
                                                Numer dokumentu
                                                {getSortIndicator(
                                                    "documentNumber"
                                                )}
                                            </th>


                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "grossAmount"
                                                    )
                                                }
                                            >
                                                Wartość brutto
                                                {getSortIndicator(
                                                    "grossAmount"
                                                )}
                                            </th>


                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "status"
                                                    )
                                                }
                                            >
                                                Płatność
                                                {getSortIndicator(
                                                    "status"
                                                )}
                                            </th>


                                            <th
                                                className="invoice-sortable-header invoice-konekto-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "konektoSmart"
                                                    )
                                                }
                                            >
                                                Konekto
                                                {getSortIndicator(
                                                    "konektoSmart"
                                                )}
                                            </th>


                                            <th>
                                                Stan
                                            </th>


                                            <th
                                                className="invoice-sortable-header"
                                                onClick={() =>
                                                    handleSort(
                                                        "notes"
                                                    )
                                                }
                                            >
                                                Komentarz
                                                {getSortIndicator(
                                                    "notes"
                                                )}
                                            </th>

                                        </tr>
                                    </thead>


                                    <tbody>

                                        {sortedDocuments.map(
                                            (document, index) => {
                                                const paymentStatus = getPaymentStatusDisplay(document);
                                                const updating = updatingDocumentId === document.id;
                                                const hasDuplicate = document.duplicateStatus === 2;
                                                const hasPossibleDuplicate = document.duplicateStatus === 1;
                                                const isPending = document.status === 1;

                                                return (
                                                    <tr
                                                        key={document.id}
                                                        className={`invoice-register-row ${updating ? "invoice-register-row-updating" : ""} ${document.isMissingOnDrive ? "invoice-missing-row" : ""} ${hasDuplicate ? "invoice-duplicate-row" : ""} ${hasPossibleDuplicate ? "invoice-possible-duplicate-row" : ""} ${isPending ? "invoice-pending-row" : ""}`}
                                                        onClick={() => handleDocumentClick(document.id)}
                                                    >

                                                        <td className="invoice-file-cell">
                                                            <div className="invoice-file-with-number">

                                                                <span className="invoice-file-number">
                                                                    {index + 1}.
                                                                </span>

                                                                <div className="invoice-file-info">
                                                                    <div className="invoice-file-name-row">
                                                                        <span className="invoice-file-link" title={document.fileName ?? ""} onClick={(event) => openDriveFile(event, document.googleDriveFileId)}>
                                                                            {document.fileName}
                                                                        </span>

                                                                        {updating && (
                                                                            <FontAwesomeIcon icon={faSpinner} spin className="invoice-row-spinner" />
                                                                        )}
                                                                    </div>

                                                                    {document.driveCreatedAt && (
                                                                        <div className="invoice-file-drive-date">
                                                                            Dysk · {formatDate(document.driveCreatedAt)}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                            </div>
                                                        </td>


                                                        <td className="invoice-date-cell">
                                                            {formatDate(
                                                                document.issueDate
                                                            )}
                                                        </td>


                                                        <td className="invoice-contractor-cell">
                                                            <span
                                                                title={
                                                                    document.contractor ??
                                                                    ""
                                                                }
                                                            >
                                                                {document.contractor ||
                                                                    "—"}
                                                            </span>
                                                        </td>


                                                        <td className="invoice-number-cell">
                                                            <span
                                                                title={
                                                                    document.documentNumber ??
                                                                    ""
                                                                }
                                                            >
                                                                {document.documentNumber ||
                                                                    "—"}
                                                            </span>
                                                        </td>


                                                        <td className="invoice-amount">
                                                            {formatAmount(
                                                                document.grossAmount,
                                                                document.currency
                                                            )}
                                                        </td>


                                                        <td className="invoice-status-cell">
                                                            <div className="invoice-payment-info">
                                                                {paymentStatus ? (
                                                                    <span
                                                                        className={`invoice-status ${paymentStatus.className}`}
                                                                        title={
                                                                            paymentStatus.isManual
                                                                                ? `Płatność potwierdzona ręcznie${
                                                                                    document.paymentManuallyConfirmedByUserName
                                                                                        ? ` przez ${document.paymentManuallyConfirmedByUserName}`
                                                                                        : ""
                                                                                }${
                                                                                    document.paymentManuallyConfirmedAt
                                                                                        ? ` · ${new Date(document.paymentManuallyConfirmedAt).toLocaleString("pl-PL")}`
                                                                                        : ""
                                                                                }`
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {paymentStatus.text}

                                                                        {paymentStatus.isManual && (
                                                                            <FontAwesomeIcon
                                                                                icon={faHandPointer}
                                                                                className="invoice-status-manual-icon"
                                                                            />
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span className="invoice-state-empty">—</span>
                                                                )}

                                                                {document.bankTransfers?.length > 0 && (
                                                                    <span
                                                                        className="invoice-transfer-count"
                                                                        title={`Powiązane przelewy: ${document.bankTransfers.length}`}
                                                                    >
                                                                        {document.bankTransfers.length}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>


                                                        <td className="invoice-konekto-cell">
                                                            <span
                                                                className={`invoice-konekto-indicator ${
                                                                    document.isUploadedToKonektoSmart
                                                                        ? "invoice-konekto-indicator-done"
                                                                        : "invoice-konekto-indicator-missing"
                                                                }`}
                                                                title={
                                                                    document.isUploadedToKonektoSmart
                                                                        ? "Wczytana do KonektoSmart"
                                                                        : "Niewczytana do KonektoSmart"
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={document.isUploadedToKonektoSmart ? faCheck : faTimes}
                                                                />
                                                            </span>
                                                        </td>


                                                        <td className="invoice-state-cell">
                                                            {document.isMissingOnDrive ? (
                                                                <span className="invoice-state invoice-state-error">
                                                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                                                    Usunięty
                                                                </span>
                                                            ) : hasDuplicate ? (
                                                                <span className="invoice-state invoice-state-error">
                                                                    <FontAwesomeIcon icon={faFile} />
                                                                    Duplikat
                                                                </span>
                                                            ) : hasPossibleDuplicate ? (
                                                                <span className="invoice-state invoice-state-warning">
                                                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                                                    Duplikat?
                                                                </span>
                                                            ) : isPending ? (
                                                                <span className="invoice-state invoice-state-pending">
                                                                    Zweryfikuj
                                                                </span>
                                                            ) : (
                                                                <span className="invoice-state-empty">—</span>
                                                            )}
                                                        </td>


                                                        <td className="invoice-notes-cell">
                                                            <span
                                                                title={
                                                                    document.notes ??
                                                                    ""
                                                                }
                                                            >
                                                                {document.notes ||
                                                                    "—"}
                                                            </span>
                                                        </td>

                                                    </tr>
                                                );
                                            }
                                        )}

                                    </tbody>

                                </table>

                            </div>
                        )}

                </>
            )}


            {activeTab === "transfers" && (
                <InvoiceTransfersOverview
                    year={year}
                    month={month}
                    statusFilter={transferStatusFilter}
                    search={transferSearch}
                />
            )}


            {selectedDocumentId != null && (
                <InvoiceDocumentModal
                    documentId={selectedDocumentId}
                    onClose={handleCloseModal}
                    onSaving={handleDocumentSaving}
                    onSaved={handleDocumentSaved}
                    onSaveError={handleDocumentSaveError}
                    onSaveBlocked={handleDocumentSaveError}
                    onRelationsChanged={handleDocumentRelationsChanged}
                />
            )}

        </div>
    );
};


export default InvoicesRegisterView;